<script setup>
import DeleteDialog from '@/views/general/DeleteDialog.vue'
import CreateNoteDrawer from '@/views/notebooks/CreateNoteDrawer.vue'
import EditNoteDrawer from '@/views/notebooks/EditNoteDrawer.vue'
import LocationDialog from '@/views/notebooks/LocationDialog.vue'
import AppDateTimePicker from '@core/components/app-form-elements/AppDateTimePicker.vue'
import { useI18n } from 'vue-i18n'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import moment from "moment";
import axios from '@/helpers/axios'
import { VCard } from 'vuetify/lib/components/index.mjs'
import {VCardText} from "vuetify/components";
import EditNotebookName from "@/components/Notebook/EditNotebookName.vue";

definePage({
  meta: {
    layout: 'default',
    role: 'user'
  },
})

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const uuid = route.params.uuid
const notebook = ref({})
const types = ref([])
const showCreateNoteDrawer = ref(false)
const isLocationDialogVisible = ref(false)
const showDeleteNotebookDialog = ref(false)
const showDeleteNoteDialog = ref(false)
const showEditNoteDrawer = ref(false)
const isLoadingMore = ref(false)

const entryLocation = ref({})

const selectedType = ref()
const dateRange = ref('')

const searchQuery = ref('')
const rowPerPage = ref(10)
const currentPage = ref(1)
const totalPage = ref(1)
const totalItems = ref(0)
const items = ref([])

const deleteNoteItem = ref(null)
const editNoteItem = ref(null)


const openDeleteNote = note => {
  deleteNoteItem.value = note
  showDeleteNoteDialog.value = true
}

const openEditNote = note => {
  editNoteItem.value = note
  showEditNoteDrawer.value = true
}

const getNotebook = () => {
  axios.get('/notebooks/' + uuid).then(response => {
    notebook.value = response.data
  }).catch(e => {
    console.error(e)
  })
}

const getNoteType = () => {
  axios.get('/notebooks/note-type').then(response => {
    types.value = response.data.data.map(function(type) {
      type.name = t('notebooks.types.' + type.key)

      return type
    })
  }).catch(e => {
    console.error(e)
  })
}


const getNotes = (isLoadMore = false) => {
  axios.get('/notebooks/' + uuid + '/notes', {
    params: {
      q: searchQuery.value,
      perPage: rowPerPage.value,
      currentPage: currentPage.value,
      type: selectedType.value,
      dates: dateRange.value,
    },
  }).then(response => {
    if (isLoadMore) {
      items.value = [...items.value, ...response.data.data]
    } else {
      items.value = response.data.data
    }
    totalPage.value = response.data.meta.last_page
    totalItems.value = response.data.meta.total
  }).catch(e => {
    console.error(e)
  })
}

const loadMore = () => {
  if (currentPage.value < totalPage.value) {
    currentPage.value += 1
    getNotes(true)
  }
}

getNotes()


watch([searchQuery, selectedType, dateRange], () => {
  currentPage.value = 1
  getNotes()
})


const deleteNotebook = () => {
  axios.delete('/notebooks/' + uuid).then(response => {
    showDeleteNotebookDialog.value = false
    toast.success(t('notebooks.deleted_notebook'), {
      autoClose: 3000,
    })

    return router.replace({ name: 'notebooks' })
  }).catch(e => {
    toast.error(t('layout.error'), {
      autoClose: 3000,
    })
  })
}

const deleteNote = () => {
  axios.delete('/notebooks/' + uuid + '/' + deleteNoteItem.value.uuid).then(response => {
    showDeleteNoteDialog.value = false
    toast.success(t('notebooks.deleted_note'), {
      autoClose: 3000,
    })

    return getNotes()
  }).catch(e => {
    toast.error(t('layout.error'), {
      autoClose: 3000,
    })
  })
}

const exportNotebook = (format) => {
  axios.get(`/notebooks/${uuid}/export?format=${format}`, {
    responseType: 'blob',
  }).then(response => {
    toast.success(t('notebooks.exporting_notebook'), {
      autoClose: 3000,
    })

    const blob = new Blob([response.data], { type: getMimeType(format) })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')

    link.href = url
    link.setAttribute('download', `${notebook.value.name}_${notebook.value.uuid}.${format}`)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }).catch(e => {
    console.error(e)
    toast.error(t('layout.error'), {
      autoClose: 3000,
    })
  })
}

// optional helper for proper MIME type
function getMimeType(format) {
  switch (format) {
    case 'csv': return 'text/csv'
    case 'pdf': return 'application/pdf'
    case 'xlsx': return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    default: return 'application/octet-stream'
  }
}

getNotebook()
getNoteType()
</script>

<template>
  <div>
    <div class="page-header mb-4">
      <div class="d-flex flex-column flex-sm-row align-sm-center justify-space-between gap-4">
        <!-- Left side: title + info -->
        <div>
          <h2 class="text-h4 font-weight-semibold mb-2 text-high-emphasis">
            {{ notebook.name }}
            <EditNotebookName :notebook="notebook" @refresh="getNotebook" class="ml-2"/>
          </h2>
          <p class="text-body-1 text-medium-emphasis mb-0">
            {{ notebook.last_entry === null ? '' : $t('notebooks.last_note', { date: moment.utc(notebook.last_entry).local().format('Y-MM-DD HH:mm') }) }}
          </p>
        </div>

        <!-- Right side: actions -->
        <div class="d-flex align-center gap-2">
          <!-- Primary action -->
          <VBtn
            color="primary"
            class="text-none px-5 py-3 font-weight-medium"
            size="large"
            rounded="lg"
            @click="showCreateNoteDrawer = true"
          >
            <VIcon start icon="tabler-plus" size="20" />
            {{ $t('notebooks.add_note') }}
          </VBtn>

          <!-- More actions menu -->
          <VMenu
            transition="fade-transition"
            location="bottom end"
            offset-y
          >
            <template #activator="{ props }">
              <VBtn
                v-bind="props"
                icon
                variant="outlined"
                color="primary"
              >
                <VIcon icon="tabler-dots-vertical" />
              </VBtn>
            </template>

            <VList density="comfortable" min-width="180">
              <VListItem @click="exportNotebook('csv')">
                <template #prepend>
                  <VIcon icon="tabler-file-type-csv" class="me-2" />
                </template>
                <VListItemTitle>{{ $t('layout.export_csv') }}</VListItemTitle>
              </VListItem>
              <VListItem @click="exportNotebook('pdf')">
                <template #prepend>
                  <VIcon icon="tabler-file-type-pdf" class="me-2" />
                </template>
                <VListItemTitle>{{ $t('layout.export_pdf') }}</VListItemTitle>
              </VListItem>

              <VDivider class="my-1" />

              <VListItem @click="showDeleteNotebookDialog = true">
                <template #prepend>
                  <VIcon icon="tabler-trash" color="error" class="me-2" />
                </template>
                <VListItemTitle class="text-error">
                  {{ $t('notebooks.delete_notebook') }}
                </VListItemTitle>
              </VListItem>
            </VList>
          </VMenu>
        </div>
      </div>
    </div>

    <VRow class="mb-4" align="center">
      <!-- Search Field -->
      <VCol cols="12" md="3">
        <VTextField
          v-model="searchQuery"
          :placeholder="$t('notebooks.search_note')"
          prepend-inner-icon="tabler-search"
          clearable
        />
      </VCol>

      <VCol cols="12" md="3">
        <VSelect
            v-model="selectedType"
            :label="$t('notebooks.select_type')"
            :items="types"
            item-title="name"
            item-value="key"
            clearable
            clear-icon="tabler-x"
          />
      </VCol>

      <VCol cols="12" md="3">
        <AppDateTimePicker
          v-model="dateRange"
          :placeholder="$t('notebooks.date')"
          :config="{ mode: 'range' }"
        />
      </VCol>
    </VRow>

    <VRow v-if="items.length" class="match-height">
      <VCol
        v-for="item in items"
        :key="item.uuid"
        cols="12"
        sm="6"
        md="4"
      >
        <VCard
          class="overflow-visible pa-4 d-flex flex-column"
          style="border-radius: 16px; height: 100%;"
          elevation="2"
        >
          <!-- Header -->
          <div class="d-flex justify-space-between align-start mb-2">
            <div>
              <h4 class="text-h5 font-weight-semibold mb-1 text-high-emphasis">
                {{ $t('notebooks.types.' + item.type) }}
              </h4>
            </div>

            <VChip color="primary">
              {{ moment.utc(item.created_at).local().format('Y-MM-DD HH:mm') }}
            </VChip>
          </div>

          <!-- Description -->
          <div v-if="item.description" class="my-2">
            <div class="d-flex align-center mt-2 mt-sm-0">
              <span class="text-subtitle-1">{{ item.description }}</span>
            </div>
          </div>

          <!-- Location -->
          <div v-if="item.location !== null" class="d-flex align-center">
            <div
              class="d-flex align-center"
              @click="entryLocation = item.location; isLocationDialogVisible = true"
            >
              <VIcon start size="18" color="warning" icon="tabler-map" />
              <h6 class="text-base">{{ $t('notebooks.location') }}</h6>
            </div>
          </div>

          <!-- Photos -->
          <div v-if="item.photos.length > 0" class="d-flex align-center mb-4">
            <VAvatar
              v-for="photo in item.photos"
              :key="photo.thumbnail_url"
              :image="photo.thumbnail_url"
              rounded="lg"
              size="x-large"
              class="mr-2"
            />
          </div>

          <!-- User (stick to bottom) -->
          <div class="d-flex justify-space-between align-center flex-wrap mt-auto">
            <div class="d-flex align-center">
              <VAvatar size="34" :image="item.user.avatar_thumbnail" />
              <span class="ms-2 font-weight-medium">{{ item.user.name }}</span>
            </div>

            <div class="d-flex align-center">
              <VMenu transition="fade-transition" location="bottom end" offset-y>
                <template #activator="{ props }">
                  <VBtn
                    v-bind="props"
                    icon
                    variant="outlined"
                    color="primary"
                    size="small"
                  >
                    <VIcon icon="tabler-dots-vertical" />
                  </VBtn>
                </template>

                <VList density="comfortable" min-width="180">
                  <VListItem @click="openEditNote(item)">
                    <template #prepend>
                      <VIcon icon="tabler-pencil" class="me-2" />
                    </template>
                    <VListItemTitle>{{ $t('layout.edit') }}</VListItemTitle>
                  </VListItem>

                  <VDivider class="my-1" />

                  <VListItem @click="openDeleteNote(item)">
                    <template #prepend>
                      <VIcon icon="tabler-trash" color="error" class="me-2" />
                    </template>
                    <VListItemTitle class="text-error">
                      {{ $t('layout.delete') }}
                    </VListItemTitle>
                  </VListItem>
                </VList>
              </VMenu>
            </div>
          </div>
        </VCard>
      </VCol>
    </VRow>


    <div
      v-else
      class="d-flex flex-column align-center justify-center text-center flex-grow-1 py-10"
    >
      <VIcon icon="tabler-database-off" size="48" color="grey" class="mb-3" />
      <h4 class="text-h6 font-weight-medium text-high-emphasis mb-1">
        {{ $t('layout.no_data') }}
      </h4>
    </div>

    <!-- Load More Button -->
    <VRow justify="center" class="mt-4">
      <VBtn
        v-if="currentPage < totalPage"
        @click="loadMore"
        :loading="isLoadingMore"
        :disabled="isLoadingMore"
      >
        {{ $t('layout.load_more') }}
      </VBtn>
    </VRow>
    

    <CreateNoteDrawer
      :is-drawer-open="showCreateNoteDrawer"
      :types="types"
      @update:is-drawer-open="(val) => showCreateNoteDrawer = val"
      @submit="getNotes"
    />

    <LocationDialog
      :is-dialog-open="isLocationDialogVisible"
      :location="entryLocation"
      @update:is-dialog-open="(val) => isLocationDialogVisible = val"
    />    

    <DeleteDialog
      :is-dialog-open="showDeleteNotebookDialog"
      :title="$t('notebooks.delete_notebook')"
      :text="$t('notebooks.delete_notebook_confirm')"
      @update:is-dialog-open="(val) => showDeleteNotebookDialog = val"
      @submit="deleteNotebook"
    />

    <DeleteDialog
      :is-dialog-open="showDeleteNoteDialog"
      :title="$t('notebooks.delete_note')"
      :text="$t('notebooks.delete_note_confirm')"
      @update:is-dialog-open="(val) => showDeleteNoteDialog = val"
      @submit="deleteNote"
    />
    <EditNoteDrawer
      v-if="editNoteItem"
      :types="types"
      :is-drawer-open="showEditNoteDrawer"
      :note="editNoteItem"
      :notebook-uuid="notebook.uuid"
      @update:is-drawer-open="(val) => showEditNoteDrawer = val"
      @submit="getNotes"
    />
  </div>
</template>

<style scoped>
.page-header {
  background: linear-gradient(180deg, var(--v-theme-surface), transparent);
  border-radius: 16px;
  padding: 1.5rem 0rem;
  transition: all 0.25s ease;
}

@media (max-width: 600px) {
  .page-header {
    padding: 1.25rem 1rem;
  }
  .page-header h2 {
    font-size: 1.4rem;
  }
}
</style>
